import classnames from 'classnames';
import React, { useEffect } from 'react';
import { toast } from 'react-toastify';
import { useHistory } from 'react-router-dom';

import ConnectAppButton from './ConnectAppButton';

import {
  Button,
  PlainButton,
  WebCopy,
  WebHeading,
  Modal,
  TokenIntegrationModalContent,
  Loader,
  Toast,
} from 'components/shared';

import Icon from 'assets/icons';
import IntegrationImage, { integrationImageById } from 'assets/img/integrations';

import { openAuthURL, getIntegrationToken, generateIntegrationOAuthUrl } from 'helpers';
import { refreshIntegrationsQuery } from 'services/apollo/queries';
import { trackingOnboardingConnectAppsView } from 'services/analytics';

import { useTranslation } from 'hooks';
import { useQuery, useApolloClient } from '@apollo/react-hooks';
import { gql } from 'apollo-boost';
import { Logger } from 'services';

import styles from './ConnectApps.module.scss';
import obstyles from '../../OnboardingScreen.module.scss';

interface ConnectAppsData {
  me: {
    userProfile: User;
    organization: Organization;
    integrations: UserIntegration[];
  };
  integrations: Integration[];
}

const ConfigureIntegrationsQuery = gql`
  query getOnboardingConnectApps {
    me {
      userProfile {
        id
      }
      organization {
        id
      }
      integrations {
        type
      }
    }
    integrations {
      type
      title
      setupPath
      image
      imageIcon
      imageProviderIcon
      userConfig {
        instructions
        fields {
          name
          label
        }
      }
      features {
        configurable
      }
    }
  }
`;

export const ConnectApps = () => {
  const { t } = useTranslation();
  const client = useApolloClient();
  const history = useHistory();

  const [modalIsOpen, setIsOpen] = React.useState(false);
  const [isExpanded, setIsExpanded] = React.useState(false);
  const [integrationToConfigure, setIntegrationToConfigure] = React.useState(null);

  useEffect(() => {
    trackingOnboardingConnectAppsView();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { error, data } = useQuery<ConnectAppsData>(ConfigureIntegrationsQuery, {
    fetchPolicy: 'cache-and-network',
  });

  if (data === undefined) return <Loader />;

  if (error) return <Loader color="Red" />;

  const { me, integrations } = data;

  const getConfigurableIntegrations = () => {
    return integrations.filter((integration) => {
      return integration.features.configurable;
    });
  };

  /**
   * Sorts the integrations
   * Configured integrations should appear first in the list by alphabetical order
   * Remaining integrations should appear after the configured by alphabetical order
   */
  const getSortedIntegrations = () => {
    return getConfigurableIntegrations().sort((a: Integration, b: Integration) => {
      const isAIntegrated = data.me.integrations.find((integration) => integration.type === a.type);
      const isBIntegrated = data.me.integrations.find((integration) => integration.type === b.type);

      if (isAIntegrated && !isBIntegrated) {
        return -1;
      } else if (!isAIntegrated && isBIntegrated) {
        return 1;
      }

      return 0;
    });
  };

  /**
   * Opens the oauth integration token modal
   */
  const openModal = () => {
    setIsOpen(true);
  };

  /**
   * Closes the oauth integration token modal
   */
  const closeModal = () => {
    setIsOpen(false);
  };

  /**
   * Expands the integrations grid visible area
   */
  const expandList = () => {
    setIsExpanded(!isExpanded);
  };

  /**
   * Sends user to the next onboarding screen
   */
  const advanceOnboarding = () => {
    history.push('/onboarding/setup-organization');
  };

  /**
   * Renders the integration icon
   * @param id: string
   */
  const getIntegrationIcon = (id: string) => {
    return integrationImageById(id, IntegrationImage, false, styles[`icon__${id}`]);
  };

  return (
    <div className={obstyles.contentLayout}>
      <div className={styles.contentWrapper}>
        <WebHeading className={styles.title} level={1}>
          {t('onboarding.connect-apps.heading')}
        </WebHeading>

        <WebCopy className={styles.copy}>
          {t('onboarding.connect-apps.text')}
        </WebCopy>
      </div>

      <div className={styles.buttonWrapper}>
        <div className={classnames(styles.buttonWrapperInner, isExpanded && styles.open)}>
          {getSortedIntegrations().map((integration: any) => {
            const id = integration.type!.slice(0, integration.type!.indexOf('@'));

            // If token integration show modal, otherwise generate OAuth url
            const configureIntegrations = () => {
              if (integration.userConfig) {
                setIntegrationToConfigure(integration);
                openModal();
              } else {
                getIntegrationToken().then(
                  (token) => {
                    const url = generateIntegrationOAuthUrl(
                      token,
                      integration.setupPath,
                      id,
                      me.userProfile.id!,
                      me.organization.id!
                    );
                    openAuthURL(url, true, '_blank', () => {
                      client
                        .query({
                          query: refreshIntegrationsQuery,
                          fetchPolicy: 'network-only',
                        })
                        .catch((error) => {
                          Logger.error(error);
                        });
                    });
                  },
                  () => {
                    toast(<Toast message={t('toasts.pop-up-blocked')} hasCloseButton />, {
                      position: toast.POSITION.BOTTOM_LEFT,
                      hideProgressBar: true,
                      closeButton: false,
                      closeOnClick: false,
                      autoClose: false,
                    });
                  }
                );
              }
            };

            return (
              <ConnectAppButton
                key={id}
                name={integration.title}
                data-integration={integration.type}
                isChecked={
                  !!me.integrations.find(
                    (userIntegration) => userIntegration.type === integration.type
                  )
                }
                onClick={configureIntegrations}
              >
                {getIntegrationIcon(id)}
              </ConnectAppButton>
            );
          })}
        </div>

        <PlainButton className={styles.moreButton} onClick={expandList}>
          {isExpanded
            ? t('onboarding.connect-apps.show-less')
            : t('onboarding.connect-apps.show-more')}
        </PlainButton>
      </div>

      <div className={obstyles.footer}>
        <Button disabled={integrations.length < 1} size="Large" onClick={advanceOnboarding}>
          {t('onboarding.continue')}
          <Icon.ChevronThick textPosition="Right" />
        </Button>
      </div>

      <Modal isOpen={modalIsOpen} onRequestClose={closeModal}>
        <TokenIntegrationModalContent
          integration={integrationToConfigure}
          closeModal={closeModal}
        />
      </Modal>
    </div>
  );
};

export default ConnectApps;
